import { useColorModeValue } from '@chakra-ui/color-mode';
import { useBreakpointValue } from '@chakra-ui/media-query';
import Link from '@tiptap/extension-link';
import { Editor as EditorType, EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import React, { useEffect, useState } from 'react';
import { filter, map } from 'rxjs/operators';
import { useContextSelector } from 'use-context-selector';
import { styled } from '../../Stitches';
import { EditorControls } from '../EditorControls';
import { useSubscription } from '../hooks/useSubscription';
import EditorContext from './EditorContext';
import { DocumentEvent } from './useDocumentState';
const StyledEditorContent = styled(EditorContent, {
  // --- Resets
  '.ProseMirror:focus': {
    outline: 'none',
  },

  width: '100%',
  // Typography
  fontFamily: 'Georgia',
  fontSize: '20px',

  marginLeft: '$4',

  h1: {
    fontSize: 32,
    marginBottom: '$4',
  },
  h2: {
    fontSize: 26,
    marginBottom: '$3',
  },

  blockquote: {
    borderLeftColor: '#9962ff',
    borderLeftWidth: '$2',

    marginLeft: '-$4',
    paddingLeft: '$4',

    marginTop: '$5',
    marginBottom: '$5',
  },

  a: {
    textDecoration: 'underline',
    cursor: 'pointer',
  },

  // Bullet and Numbered Lists
  ul: {
    marginLeft: 18,
  },
  'li > ul': {
    marginLeft: '$6',
  },
  ol: {
    marginLeft: 18,
  },
  'li > ol': {
    marginLeft: '$6',
  },

  '* > li': {
    marginTop: '$3',
  },

  code: {
    fontFamily: '$mono',
  },

  // Code
  'p > code': {
    fontSize: '0.9em',
    color: 'white',
    px: '$3',
    py: '$2',
    borderRadius: '$3',
  },

  pre: {
    fontFamily: '$mono',
    color: 'white',
    fontSize: '0.9em',

    borderRadius: '$3',
    padding: '$5',

    // Warning! Visually check before changing these.
    // The number can't be the same as margin left
    marginLeft: '-$5',
    marginRight: -8,

    marginTop: '$5',
    marginBottom: '$5',
  },

  variants: {
    colorTheme: {
      light: {
        pre: {
          background: '#161424',
          caretColor: '#fff',
        },
        'p > code': {
          backgroundColor: '#1a1729',
          caretColor: '#fff',
        },
      },
      dark: {
        pre: {
          background: '#161424',
          borderColor: '#1b192c',
          borderWidth: 1,
        },
        'p > code': {
          color: 'black',
          backgroundColor: '#dddddd',
        },
      },
    },

    narrow: {
      true: {
        pre: {
          borderRadius: 0,
        },
      },
    },
  },
});

const ThemedEditor = ({
  editor,
  children,
}: {
  editor: EditorType | null;
  children?: React.ReactNode;
}) => {
  const theme = useColorModeValue('light', 'dark');
  const narrow = useBreakpointValue({ base: true, md: false });

  return (
    <StyledEditorContent colorTheme={theme} narrow={narrow} editor={editor}>
      {children}
    </StyledEditorContent>
  );
};

const useInitialiseValue = () => {
  const bus$ = useContextSelector(EditorContext, (ctx) => ctx.bus$);
  const [value, setValue] = useState<string | undefined>(undefined);

  useSubscription(
    () =>
      bus$
        .pipe(
          filter((e) => e.type === 'Document'),
          map((e) => (e as { type: 'Document'; event: DocumentEvent }).event)
        )
        .subscribe((event) => event.type === 'INITIALISE' && setValue(event.document.body)),
    [bus$, setValue]
  );

  return value;
};

export const Editor = () => {
  const dispatch = useContextSelector(EditorContext, (ctx) => ctx.dispatch);
  const body = useContextSelector(EditorContext, (ctx) => ctx.document?.body);

  const initialiseValue = useInitialiseValue();

  const updateBody = (body: string) =>
    dispatch({ type: 'Document', event: { type: 'BODY_UPDATED', body } });

  const editor = useEditor({
    editable: false,
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2],
        },
        horizontalRule: false,
      }),
      Link.configure({
        linkOnPaste: true,
      }),
    ],

    content: body,
    onUpdate: ({ editor }) => updateBody(editor.getHTML()),
  });

  useEffect(() => {
    if (initialiseValue && editor) {
      editor.commands.setContent(initialiseValue, false);
      editor.setEditable(true);
    }
  }, [initialiseValue, editor]);

  return (
    <ThemedEditor editor={editor}>{editor && <EditorControls editor={editor} />}</ThemedEditor>
  );
};

export const ReadOnlyEditor = ({ content }: { content: string }) => {
  const editor = useEditor({
    content: content,
    editable: false,
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2],
        },
        horizontalRule: false,
      }),
    ],
  });

  return <ThemedEditor editor={editor} />;
};
